import { useState, useContext, useEffect, useMemo } from 'react';
import "../../../node_modules/video-react/dist/video-react.css";
import { ReactComponent as Logo } from 'assets/logo.svg';
import iconReturnMap from 'assets/歸還地圖.png';
import iconStartReturn from 'assets/開始歸還.png';
import iconReturnFlow from 'assets/歸還流程.png';
import iconFaq from 'assets/常見問答.png';
import iconFamilyMart from 'assets/全家便利商店.png';
import iconOtherReturn from 'assets/其他歸還點.png';
import giftboxFlattenVideo from 'assets/giftbox-flatten-tutorial.mp4';
import giftboxFlattenGuide1 from 'assets/giftbox-flatten-guide-1.jpg';
import giftboxFlattenGuide2 from 'assets/giftbox-flatten-guide-2.jpg';
import giftboxFlattenGuide3 from 'assets/giftbox-flatten-guide-3.jpg';
import { useNavigate, useLocation } from 'react-router';
import Dialog from 'components/Dialog';
import {
  LINE_OFFICIAL_URL,
  ECAN_DELIVERY_FORM_URL,
} from 'constants/externalLinks';
import { GlobalContext } from '../../storage';
import ReturnFlowNotice, {
  hasSeenReturnFlowNotice,
  markReturnFlowNoticeSeen,
} from './returnFlowNotice';
import styles from './rentHome.module.scss';

const Rent = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  // 歸還流程彈窗
  const [flowDialogVisible, setFlowDialogVisible] = useState<boolean>(false);
  // 禮盒攤平教學影片彈窗（綠色歸還袋流程才有）
  const [tutorialDialogVisible, setTutorialDialogVisible] = useState<boolean>(false);
  // 教學圖片點開放大（第一張是「歸還說明」圖含文字，需放大閱讀）
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  // 教學圖片：第一張為含文字的歸還說明圖，後兩張為實體攤平示意
  const tutorialImages = [
    { src: giftboxFlattenGuide1, alt: '循環禮盒歸還說明' },
    { src: giftboxFlattenGuide2, alt: '禮盒攤平裝入歸還袋' },
    { src: giftboxFlattenGuide3, alt: '禮盒攤平示意' },
  ];
  // 「開始歸還」後顯示通路選擇子畫面（暫時沿用舊流程：全家 / 其他通路）
  const [channelPickerVisible, setChannelPickerVisible] = useState<boolean>(false);
  // 新版歸還流程通知：點「開始歸還」先擋一次教學圖，看完才進通路選擇
  const [flowNoticeVisible, setFlowNoticeVisible] = useState<boolean>(false);

  const startReturn = () => {
    if (hasSeenReturnFlowNotice()) {
      setChannelPickerVisible(true);
      return;
    }
    setFlowNoticeVisible(true);
  };

  const closeFlowNotice = () => {
    markReturnFlowNoticeSeen();
    setFlowNoticeVisible(false);
    setChannelPickerVisible(true);
  };

  // 綠色歸還袋專屬入口：QR 連到 /rent?source=greenbag。
  // 只有從綠袋進來才在「選擇歸還通路」頁顯示宅配通 + 禮盒攤平教學；
  // 一般 C 端包裝(走 /rent)不會帶這個參數，維持原本兩個通路選項。
  const isGreenBag = useMemo(() => {
    try {
      return new URLSearchParams(search).get('source') === 'greenbag';
    } catch {
      return false;
    }
  }, [search]);

  // /rent 是歸還流程的起點 — 清掉上一次留在 returnStorage 的 channel / service point 資訊,
  // 避免使用者完成一輪歸還後再點「開始歸還」時,return.tsx 的 mount useEffect 誤用殘留的
  // chs_short_code/store_id 命中 else-if 分支 → 直接 setStep(1) 跳過掃歸還點 QR Code。
  // (Lynn 同仁回報:不領取優惠券 → 首頁 → 再點開始歸還 → 直接進包裝掃描,跳過歸還點)
  // @ts-ignore — GlobalContext 是 JS,沒 type
  const { returnStorage } = useContext(GlobalContext);
  useEffect(() => {
    try {
      returnStorage.setChannelName(undefined);
      returnStorage.setChannelId(undefined);
      returnStorage.setSvPtName(undefined);
      returnStorage.setSvPtId(undefined);
      returnStorage.setStoreId(undefined);
      returnStorage.setChsShortCode(undefined);
      returnStorage.setEpkId(undefined);
      returnStorage.setTypeName(undefined);
      returnStorage.setDateTime(undefined);
    } catch (e) {
      console.log('reset returnStorage failed', e);
    }
    // 只要 mount 時清一次,後續進 /return 掃到新店家會重新填
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PG-92: 全家鈕對齊 LINE 圖文選單的 canonical URL,直接走 /return/fami/fami99999。
  // 舊路 ?from=fami 落在 step 0 掃描畫面但沒「無法掃描」逃生鈕,使用者只能站在那邊。
  // /return/fami/fami99999 由 return.tsx 既有 effect 自動 setManualVisible+getLocation,
  // 跟 LINE rich menu 入口完全一致(Lynn 04/29 確認 LINE 圖文選單能自動抓最近門市)。
  const goFamily = () => navigate('/return/fami/fami99999');
  const goOther = () => navigate('/return');

  // 點地圖：直接在 web 上開 /nearby，不跳 LIFF
  // 後端 listServicePoints / listChannels 已改為公開 API，未登入也能使用
  const goMap = () => {
    navigate('/nearby');
  };

  // 常見問答：非 momo 範圍，走 100% 可靠的 OA URL。使用者到 OA 後自行點 rich menu。
  const goProblem = () => {
    window.location.href = LINE_OFFICIAL_URL;
  };

  // 宅配通（綠色歸還袋專屬）：導去 Google 表單填宅配寄件資料，
  // 答覆由 management-backend 的宅配通 intake 排程接手自動建單。
  const goEcanDelivery = () => {
    window.location.href = ECAN_DELIVERY_FORM_URL;
  };

  return (
    <div className={styles.rentHome}>
      {!channelPickerVisible && (
        <>
          <div className={styles.brandBar}>
            <Logo />
          </div>

          <div className={styles.header}>
            <h1>循環包裝歸還</h1>
            <p>選擇下方功能，開始您的歸還旅程</p>
          </div>

          <div className={styles.cardList}>
            {/* 主要 CTA #1：歸還地圖 */}
            <button
              type="button"
              className={styles.primaryCard}
              onClick={goMap}
            >
              <div className={styles.textWrap}>
                <span className={styles.title}>歸還地圖</span>
                <span className={styles.subtitle}>查看附近可歸還的據點</span>
              </div>
              <div className={`${styles.iconWrap} ${styles.iconWrapPlain}`}>
                <img src={iconReturnMap} alt="歸還地圖" />
              </div>
            </button>

            {/* 主要 CTA #2：開始歸還 */}
            <button
              type="button"
              className={styles.primaryCard}
              onClick={startReturn}
            >
              <div className={styles.textWrap}>
                <span className={styles.title}>開始歸還</span>
                <span className={styles.subtitle}>
                  掃描歸還點與包裝編號 QR Code
                </span>
              </div>
              <div className={`${styles.iconWrap} ${styles.iconWrapPlain}`}>
                <img src={iconStartReturn} alt="開始歸還" />
              </div>
            </button>

            <div className={styles.secondaryGrid}>
              {/* 次要：歸還流程 */}
              <div
                className={styles.secondaryCard}
                onClick={() => setFlowDialogVisible(true)}
                role="button"
                tabIndex={0}
              >
                <div className={styles.iconWrap}>
                  <img src={iconReturnFlow} alt="歸還流程" />
                </div>
                <div>
                  <div className={styles.title}>歸還流程</div>
                  <div className={styles.subtitle}>查看歸還步驟說明</div>
                </div>
              </div>

              {/* 次要：常見問答 */}
              <div
                className={styles.secondaryCard}
                onClick={goProblem}
                role="button"
                tabIndex={0}
              >
                <div className={styles.iconWrap}>
                  <img src={iconFaq} alt="常見問答" />
                </div>
                <div>
                  <div className={styles.title}>常見問答</div>
                  <div className={styles.subtitle}>查看常見問題</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {channelPickerVisible && (
        <>
          <div className={styles.brandBar}>
            <Logo />
          </div>

          <div className={styles.channelPicker}>
            <div className={styles.header}>
              <h1>選擇歸還通路</h1>
              <p>請選擇要歸還的通路類型</p>
            </div>

            {/* 宅配通：只有掃綠色歸還袋(source=greenbag)才出現，其他 C 端包裝無此選項 */}
            {isGreenBag && (
              <button
                type="button"
                className={styles.primaryCard}
                onClick={goEcanDelivery}
              >
                <div className={styles.textWrap}>
                  <span className={styles.title}>宅配通</span>
                  <span className={styles.subtitle}>宅配到府寄件</span>
                </div>
                <div className={`${styles.iconWrap} ${styles.iconWrapPlain}`}>
                  <span className={styles.emojiIcon} role="img" aria-label="宅配通">
                    🚚
                  </span>
                </div>
              </button>
            )}

            <button
              type="button"
              className={styles.primaryCard}
              onClick={goFamily}
            >
              <div className={styles.textWrap}>
                <span className={styles.title}>全家便利商店</span>
                <span className={styles.subtitle}>FamiPort 機台寄件</span>
              </div>
              <div className={`${styles.iconWrap} ${styles.iconWrapPlain}`}>
                <img src={iconFamilyMart} alt="全家便利商店" />
              </div>
            </button>

            <button
              type="button"
              className={styles.primaryCard}
              onClick={goOther}
            >
              <div className={styles.textWrap}>
                <span className={styles.title}>其他歸還點</span>
                <span className={styles.subtitle}>掃描歸還點 QR code</span>
              </div>
              <div className={`${styles.iconWrap} ${styles.iconWrapPlain}`}>
                <img src={iconOtherReturn} alt="其他歸還點" />
              </div>
            </button>

            {/* 禮盒攤平教學：綠色歸還袋流程才有，點開看教學影片，協助使用者選通路前先攤平 */}
            {isGreenBag && (
              <button
                type="button"
                className={styles.tutorialBtn}
                onClick={() => setTutorialDialogVisible(true)}
              >
                <span className={styles.tutorialText}>禮盒攤平教學</span>
                <span className={styles.tutorialPlay} aria-hidden="true">
                  ▶
                </span>
              </button>
            )}

            <button
              type="button"
              className={styles.backBtn}
              onClick={() => setChannelPickerVisible(false)}
            >
              返回上一頁
            </button>
          </div>
        </>
      )}

      <ReturnFlowNotice visible={flowNoticeVisible} onClose={closeFlowNotice} />

      {/* 首頁「歸還流程」按鈕:與歸還前的更新通知共用同一張教學圖,
          避免兩處各維護一份步驟文字、改版時漏改其中一邊(舊的文字版就是這樣過期的) */}
      <ReturnFlowNotice
        visible={flowDialogVisible}
        mode="guide"
        onClose={() => setFlowDialogVisible(false)}
      />

      <Dialog
        visible={tutorialDialogVisible}
        onConfirm={() => setTutorialDialogVisible(false)}
        showOk={false}
        message={
          <div className={styles.tutorialDialog}>
            <h2>禮盒攤平教學</h2>

            {/* 直式教學影片：手機直握觀看較符合習慣。用 playsInline 避免 iOS 全螢幕接管 */}
            <div className={styles.videoWrap}>
              <video
                className={styles.video}
                src={giftboxFlattenVideo}
                controls
                playsInline
                preload="metadata"
              />
            </div>

            {/* 教學圖片：點縮圖開 lightbox 放大（第一張含文字說明） */}
            <div className={styles.tutorialImages}>
              {tutorialImages.map((img) => (
                <button
                  key={img.src}
                  type="button"
                  className={styles.tutorialImageThumb}
                  onClick={() => setZoomImage(img.src)}
                >
                  <img src={img.src} alt={img.alt} loading="lazy" />
                </button>
              ))}
            </div>
            <p className={styles.tutorialImageHint}>點圖片可放大閱讀</p>

            <div className={styles.closeBtnWrap}>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => setTutorialDialogVisible(false)}
              >
                關閉
              </button>
            </div>
          </div>
        }
      />

      {/* 教學圖片放大 lightbox：點任一縮圖全螢幕放大，點背景或關閉鈕收合 */}
      {zoomImage && (
        <div
          className={styles.imageLightbox}
          onClick={() => setZoomImage(null)}
          role="dialog"
          aria-label="放大圖片"
        >
          <button
            type="button"
            className={styles.imageLightboxClose}
            onClick={() => setZoomImage(null)}
            aria-label="關閉"
          >
            ×
          </button>
          {/* 圖片本身吃掉點擊，避免點圖片誤觸背景關閉 */}
          <img
            src={zoomImage}
            alt="放大教學圖片"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default Rent;
